// ============================================================================
// Accessible properties: owned + co-hosting (unified for property switcher)
// ============================================================================

import { useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useUserCapabilities } from "@/hooks/useUserCapabilities";
import { useProperties } from "@/hooks/useProperties";
import { useCohostProperties } from "@/hooks/useCohostProperties";

export type PropertyAccessType = "owner" | "cohost";

export interface AccessibleProperty {
  id: string;
  name: string;
  address?: string | null;
  city?: string | null;
  property_photo_url?: string | null;
  property_type?: string | null;
  shortAddress?: string | null;
  accessType?: PropertyAccessType;
}

export function useAccessibleProperties(): {
  properties: AccessibleProperty[];
  isLoading: boolean;
  isOwner: boolean;
  canCreateProperties: boolean;
  showSwitcher: boolean;
} {
  const { user } = useAuth();
  const { is_owner, can_create_properties } = useUserCapabilities();
  const { data: ownerProperties = [], isLoading: ownerLoading } = useProperties();
  const { data: cohostProperties = [], isLoading: cohostLoading } = useCohostProperties();

  return useMemo(() => {
    const ownerList: AccessibleProperty[] = (
      ownerProperties as {
        id: string;
        name: string;
        address?: string | null;
        city?: string | null;
        property_photo_url?: string | null;
        property_type?: string | null;
        unit?: string | null;
      }[]
    ).map((p) => {
      const shortAddress = p.address?.trim() || (p.unit && p.city ? `${p.unit}, ${p.city}` : p.city) || null;
      return {
        id: p.id,
        name: p.name,
        address: p.address ?? null,
        city: p.city ?? null,
        property_photo_url: p.property_photo_url ?? null,
        property_type: p.property_type ?? null,
        shortAddress,
        accessType: "owner" as const,
      };
    });

    const ownerIds = new Set(ownerList.map((p) => p.id));
    const cohostList: AccessibleProperty[] = cohostProperties
      .filter((p) => !ownerIds.has(p.id))
      .map((p) => {
        const shortAddress = p.address?.trim() || (p.unit && p.city ? `${p.unit}, ${p.city}` : p.city) || null;
        return {
          id: p.id,
          name: p.name,
          address: p.address ?? null,
          city: p.city ?? null,
          property_photo_url: p.property_photo_url ?? null,
          property_type: p.property_type ?? null,
          shortAddress,
          accessType: "cohost" as const,
        };
      });

    const properties = [...ownerList, ...cohostList];

    return {
      properties,
      isLoading: !!user && (ownerLoading || cohostLoading),
      isOwner: is_owner,
      canCreateProperties: can_create_properties,
      showSwitcher: properties.length > 0,
    };
  }, [
    user,
    is_owner,
    can_create_properties,
    ownerProperties,
    ownerLoading,
    cohostProperties,
    cohostLoading,
  ]);
}
